/* eslint-disable @next/next/no-img-element */
import React, { useImperativeHandle, useState, useMemo, useEffect, useRef } from "react";
import { useTranslations } from 'next-intl';
import toast from "react-hot-toast";
import html2canvas from "html2canvas";
import { Env } from '@/libs/env';
import cn from "@/utils/classnames";
import { isInApp } from '@/libs/comeAppSDK';
import { setUrlQueryParams } from "@/utils/url";
import { ShareSourceEnum } from "@/types/invited";
import QRCode from "../QRCode";
import { saveImageInApp } from "@/utils/saveImg";

interface PosterProps {
  id?: string;
  className?: string;
  poster: {
    url: string;
    width?: number;
    height?: number;
    className?: string;
    auto?: boolean; // 是否保留比例铺满
  };
  qrcode: {
    url: string;
    size?: number | string;
    className?: string;
  };
  logo?: {
    containerClassName?: string;
    width?: number;
    height?: number;
    className?: string;
  };
  bottom?: {
    className?: string;
    download?: boolean;
  };
  /** 
   * 下载图片的目标宽度（像素）
   * 默认：1080px（适合社交媒体分享）
   */
  downloadWidth?: number;
  /** 
   * 下载图片的目标高度（像素）
   * 默认：1920px（与 1080x1920 竖版常用尺寸匹配）
   */
  downloadHeight?: number;
  /**
   * 渲染模式：contain（保留比例，留边）或 cover（裁剪填满）
   * 默认：contain
   */
  fitMode?: 'contain' | 'cover';
  /**
   * 画布背景色（当使用 contain 时留边区域的底色）
   * 默认：#ffffff
   */
  backgroundColor?: string;
  /**
   * @des 是否是缩略图
   */
  isThumbnail?: boolean;
  inviteCode: string;
}

export interface PosterApi {
  handleDownloadPoster: () => void;
  getContainer: () => HTMLDivElement | null;
  getDownloadLinkId: () => string;
}

const DOWNLOAD_LINK_ID = 'download-poster-link';

/**
 * 底部信息栏内容组件
 */
const PosterBottomInfo: React.FC<{ inviteCode: string; className?: string; highTextClz?: string; itemClz?: string; download?: boolean; }> = ({ inviteCode, className = '', download = false }) => {
  const t = useTranslations('referral');
  return (
    <div id="share-poster-bottom-info" className="absolute bottom-0 left-0 right-0 w-full h-fit bg-black/60 font-medium">
      <div className={`${download ? 'py-[25px] px-[10px] space-y-[10px]' : 'py-[20px] px-[5px] space-y-[6px]'} ${className}`}>
        <div className={`flex ${download ? 'leading-none items-start' : 'items-center leading-[9px]'}`}>
          <div className="text-secondary mr-[5px] text-[11px]">{t('googleSearch')}:</div>
          <div className={`text-accent-yellow ${download ? 'text-[15px] -mt-[5px]' : 'text-[12px]'}`}>{Env.NEXT_PUBLIC_APP_DOMAIN}</div>
        </div>
        <div className={`flex ${download ? 'leading-none items-start' : 'items-center leading-[9px]'}`}>
          <div className="text-secondary mr-[5px] text-[11px]">{t('referralCode')}:</div>
          <div className={`text-accent-yellow ${download ? 'text-[15px] -mt-[5px]' : 'text-[12px]'}`}>{inviteCode}</div>
        </div>
      </div>
    </div>
  );
};

const Poster = React.forwardRef<PosterApi, PosterProps>((props: PosterProps, ref) => {
  const {
    poster, qrcode, logo, className, id: propsId, downloadWidth = 720, downloadHeight = 1280, fitMode = 'contain',
    backgroundColor = 'transparent', isThumbnail = true, inviteCode, bottom
  } = props;
  const t = useTranslations('referral');
  // 使用 useMemo 确保 id 在组件生命周期内保持稳定
  const id = useMemo(() => propsId || `poster-container-${Math.random().toString(36).substr(2, 9)}`, [propsId]);
  const [isDownloading, setIsDownloading] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const clonedElementRef = useRef<HTMLElement | null>(null);

  // 准备克隆元素的函数，支持复用
  const prepareClonedElement = (posterElement: HTMLElement): HTMLElement => {
    let clonedElement = clonedElementRef.current;

    // 如果克隆元素不存在，创建新的
    if (!clonedElement) {
      clonedElement = posterElement.cloneNode(true) as HTMLElement;

      // 将克隆的元素添加到 DOM 中（隐藏）
      clonedElement.style.position = 'absolute';
      clonedElement.style.left = '-9999px';
      clonedElement.style.top = '-9999px';
      document.body.appendChild(clonedElement);
      clonedElementRef.current = clonedElement;
    }

    return clonedElementRef.current!;
  };

  // 组件卸载时清理克隆元素
  useEffect(() => {
    return () => {
      if (clonedElementRef.current && clonedElementRef.current.parentNode) {
        clonedElementRef.current.parentNode.removeChild(clonedElementRef.current);
        clonedElementRef.current = null;
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    handleDownloadPoster: async () => {
      // 检查是否正在下载
      if (isDownloading) {
        toast.loading(t('posterGenerating'));
        return;
      }

      let loadingToast: string | undefined;
      try {
        setIsDownloading(true);
        loadingToast = toast.loading(t('posterGenerating'));

        // 获取海报容器元素
        const posterElement = document.getElementById(id);
        if (!posterElement) {
          throw new Error('Poster container not found');
        }

        // 准备克隆元素（复用或创建）
        const clonedElement = prepareClonedElement(posterElement);

        // 处理 Canvas 元素（二维码）- 从原始元素获取 Canvas 内容
        const originalCanvases = posterElement.querySelectorAll('canvas');
        const clonedCanvases = clonedElement.querySelectorAll('canvas');

        originalCanvases.forEach((originalCanvas, index) => {
          const clonedCanvas = clonedCanvases[index];
          if (clonedCanvas) {
            // 从原始 Canvas 获取内容
            const dataURL = originalCanvas.toDataURL('image/png');

            // 创建图片替换克隆的 Canvas
            const img = document.createElement('img');
            img.src = dataURL;
            img.width = originalCanvas.width;
            img.height = originalCanvas.height;
            img.style.cssText = clonedCanvas.style.cssText;
            img.className = clonedCanvas.className;

            // 替换克隆的 Canvas
            clonedCanvas.parentNode?.replaceChild(img, clonedCanvas);
          }
        });

        // ✅ 固定克隆元素的宽度，防止 scale 导致布局错乱（每次下载时更新）
        const originalWidth = posterElement.offsetWidth;
        const originalHeight = posterElement.offsetHeight;
        clonedElement.style.width = `${originalWidth}px`;
        clonedElement.style.height = `${originalHeight}px`;

        // ✅ 计算合适的 scale 以达到目标宽度（用于提高清晰度，不改变布局）
        const currentWidth = clonedElement.offsetWidth;
        const targetScale = downloadWidth / currentWidth;

        // 限制 scale 在合理范围内（1-8）
        const scale = Math.max(1, Math.min(8, targetScale));

        const sourceCanvas = await html2canvas(clonedElement, {
          backgroundColor: backgroundColor,
          scale, // 动态计算 scale
          allowTaint: true,
          useCORS: true,
          logging: false,
          imageTimeout: 15000,
        });

        // ✅ 将源画布按固定尺寸绘制到目标画布，保证导出尺寸一致
        const targetCanvas = document.createElement('canvas');
        targetCanvas.width = downloadWidth;
        targetCanvas.height = downloadHeight;
        const ctx = targetCanvas.getContext('2d');
        if (!ctx) {
          throw new Error('Canvas context not available');
        }

        // 填充背景色
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, downloadWidth, downloadHeight);

        const srcW = sourceCanvas.width;
        const srcH = sourceCanvas.height;
        const scaleContain = Math.min(downloadWidth / srcW, downloadHeight / srcH);
        const scaleCover = Math.max(downloadWidth / srcW, downloadHeight / srcH);
        const scaleFinal = fitMode === 'cover' ? scaleCover : scaleContain;
        const drawW = Math.round(srcW * scaleFinal);
        const drawH = Math.round(srcH * scaleFinal);
        const offsetX = Math.round((downloadWidth - drawW) / 2);
        const offsetY = Math.round((downloadHeight - drawH) / 2);

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(sourceCanvas, 0, 0, srcW, srcH, offsetX, offsetY, drawW, drawH);

        const filename = `${Env.NEXT_PUBLIC_BRAND}-poster.png`;
        const isApp = isInApp();
        targetCanvas.toBlob(async (blob) => {
          if (!blob) {
            if (loadingToast) {
              toast.dismiss(loadingToast);
            }
            toast.error(t('downloadFailed'));
            return;
          }

          if (isApp) {
            try {
              const status = await saveImageInApp(blob, filename);
              toast.dismiss(loadingToast);
              if (status) {
                toast.success(t('downloadSuccess'));
              } else {
                toast.error(t('downloadFailed'));
              }
            } catch {
              toast.dismiss(loadingToast);
              toast.error(t('downloadFailed'));
            }
            return;
          }

          try {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.id = DOWNLOAD_LINK_ID;
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            setTimeout(() => {
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }, 0);

            if (loadingToast) {
              toast.dismiss(loadingToast);
            }
            toast.success(t('downloadSuccess'));
          } catch {
            if (loadingToast) {
              toast.dismiss(loadingToast);
            }
            toast.error(t('downloadFailed'));
          }
        }, 'image/png', 0.8);
      } catch {
        if (loadingToast) {
          toast.dismiss(loadingToast);
        }
        toast.error(t('downloadFailed'));
      } finally {
        setIsDownloading(false);
      }
    },
    getContainer: () => containerRef.current,
    getDownloadLinkId() {
      return DOWNLOAD_LINK_ID;
    }
  }));

  return (
    <div id={id} ref={containerRef} className={cn("w-36 h-[184px] rounded-xl relative overflow-hidden border-solid border-[#272D37] border-[1px]", className)} >
      {poster?.auto ? (
        <div className="overflow-hidden">
          <img
            className={cn("w-full h-full", poster.className)}
            width={Number(poster.width) ? Number(poster.width) : 144}
            height={Number(poster.height) ? Number(poster.height) : 184}
            src={poster.url || ''}
            alt="share"
            loading="eager"
          />
        </div>
      ) : (
        <img
          className={cn("w-full h-full object-cover", poster.className)}
          width={Number(poster.width) ? Number(poster.width) : 144}
          height={Number(poster.height) ? Number(poster.height) : 184}
          src={poster.url || ''}
          alt="share"
          loading="eager"
        />
      )}
      {!isThumbnail && <PosterBottomInfo inviteCode={inviteCode} {...bottom} />}
      {/* Logo - 左上角 */}
      <div className={cn('absolute top-2.5 left-2.5', logo?.containerClassName)} >
        <img
          src={`${Env.NEXT_PUBLIC_BRAND_ASSETS_BASE}/logo.png?v=${Env.NEXT_PUBLIC_BRAND_ASSETS_VERSION}`}
          alt="logo"
          width={logo?.width || 32}
          height={logo?.height || 32}
          className={cn("object-contain", logo?.className)}
          loading="eager"
        />
      </div>
      {/* 二维码 - 右下角 */}
      <div className={cn("absolute bottom-[5px] right-0 rounded-lg p-[2px] bg-white", !isThumbnail && "bottom-4 right-2 w-[70px] h-[70px]", qrcode.className)} id={`${id}-qrcode`}>
        <QRCode
          url={qrcode.url ? setUrlQueryParams(qrcode.url, { s: ShareSourceEnum.QR }) : ''}
          size={qrcode.size || 60}
          className="w-full h-full rounded-lg"
        />
      </div>
    </div>
  );
});

Poster.displayName = "Poster";

export default Poster;